import { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { meters, monitoring } from '../../../services/api';
import { ThunderboltOutlined } from '@ant-design/icons';

export default function Monitoring() {
  const [loading, setLoading] = useState(true);
  const [meterList, setMeterList] = useState([]);
  const [pqData, setPqData] = useState(null);
  const [voltageCurve, setVoltageCurve] = useState(null);

  useEffect(() => {
    loadMeters();
    const timer = setInterval(loadMeters, 5000);
    return () => clearInterval(timer);
  }, []);

  const inFlightRef = useRef(false);

  const loadMeters = async () => {
    // 轮询竞态守卫：上一轮未返回时跳过本轮，避免慢请求重叠乱序
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const metersRes = await meters.list();
      const ids = (metersRes.data || []).map(m => m.id);
      if (ids.length === 0) {
        setMeterList([]);
        setVoltageCurve(null);
        inFlightRef.current = false;
        return;
      }
      const realtimeRes = await monitoring.batchRealtime({ meter_ids: ids });
      const rows = (realtimeRes.data || []).map(item => ({
        id: item.meter_id,
        name: item.meter_name || `表计#${item.meter_id}`,
        type: item.type,
        active_power: item.active_power ?? item.charge_power ?? item.discharge_power ?? item.charging_power,
        voltage: item.voltage_a ?? item.voltage,
        current: item.current_a ?? item.current,
        power_factor: item.power_factor,
        thd: item.thd_voltage ?? item.thd_current,
        inverter_temp: item.inverter_temp ?? item.cell_temp,
        soc: item.soc,
        status: item.inverter_status || item.status || item.connector_status,
      }));
      setMeterList(rows);

      if (ids[0]) {
        const [pqRes, curveRes] = await Promise.all([
          monitoring.powerQuality(ids[0]),
          monitoring.curve({ meter_id: ids[0], hours: 1, fields: 'voltage_a,voltage_b,voltage_c' }),
        ]);
        setPqData(pqRes.data);
        setVoltageCurve(curveRes.data);
      }
    } catch (err) {
      // 静默刷新，避免每5秒弹窗
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const columns = [
    { title: '设备名称', dataIndex: 'name', fixed: 'left' },
    { title: '类型', dataIndex: 'type', render: t => <Tag>{t}</Tag> },
    { title: '有功功率(kW)', dataIndex: 'active_power', render: v => v != null ? Math.abs(v).toFixed(2) : '-', sorter: (a, b) => Math.abs(a.active_power||0) - Math.abs(b.active_power||0) },
    { title: '电压(V)', dataIndex: 'voltage', render: v => v?.toFixed?.(1) ?? v ?? '-' },
    { title: '电流(A)', dataIndex: 'current', render: v => v?.toFixed?.(1) ?? v ?? '-' },
    { title: '功率因数', dataIndex: 'power_factor', render: v => v?.toFixed?.(3) ?? '-' },
    { title: 'THD(%)', dataIndex: 'thd', render: v => v?.toFixed?.(1) ?? '-' },
    { title: '温度(℃)', dataIndex: 'inverter_temp', render: v => v?.toFixed?.(1) ?? '-' },
    { title: 'SOC(%)', dataIndex: 'soc', render: v => v?.toFixed?.(1) ?? '-' },
    { title: '状态', dataIndex: 'status', render: v => v ? <Tag color={v === '运行' || v === '放电' || v === '在线' ? 'green' : 'blue'}>{v}</Tag> : '-' },
  ];

  const curvePoints = voltageCurve?.points || [];
  const hasCurve = curvePoints.some(p => p.voltage_a != null || p.voltage_b != null || p.voltage_c != null);

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['A相电压', 'B相电压', 'C相电压'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: curvePoints.map(p => p.time) },
    yAxis: { type: 'value', name: 'V' },
    series: [
      { name: 'A相电压', type: 'line', data: curvePoints.map(p => p.voltage_a), smooth: true, symbol: 'none', connectNulls: true },
      { name: 'B相电压', type: 'line', data: curvePoints.map(p => p.voltage_b), smooth: true, symbol: 'none', connectNulls: true },
      { name: 'C相电压', type: 'line', data: curvePoints.map(p => p.voltage_c), smooth: true, symbol: 'none', connectNulls: true },
    ]
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={8}><Card size="small"><Statistic title="在线设备" value={meterList.length} suffix="台" prefix={<ThunderboltOutlined />} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="电压不平衡" value={pqData?.voltage_unbalance ?? 0} suffix="%" precision={2} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="THD电压" value={pqData?.thd_voltage_total ?? 0} suffix="%" precision={2} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Card title="实时遥测数据（每5秒自动刷新）" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={meterList} rowKey="id" pagination={false} size="small" scroll={{ x: 1000 }} />
      </Card>
      <Card title={`三相电压实时曲线${voltageCurve?.meter_name ? `（${voltageCurve.meter_name}）` : ''}`} style={{ marginTop: 16 }} size="small">
        {hasCurve
          ? <ReactEChartsCore option={chartOption} style={{ height: 250 }} />
          : <Empty description="暂无历史遥测数据" style={{ padding: '40px 0' }} />}
      </Card>
    </div>
  );
}
